"use client";

import { resolveToolName } from "@health/types/ui-tools";
import { type ChatLoadingItem, SkeletonBlock, SkeletonLine, SkeletonStack } from "@health/ui";
import { AuthWidgetSkeleton } from "./AuthWidget";
// Import skeletons from their dedicated .skeleton.tsx files — never from the
// main widget file — so the skeleton registry stays independent of the
// lazy-loaded widget chunks. If you import from the main widget here, the
// React.lazy boundary collapses and the full widget lands in the main bundle.
import { BMIResultWidgetSkeleton } from "./BMIResultWidget.skeleton";
import { ChipGroupWidgetSkeleton, ChipsWidgetSkeleton } from "./ChipsWidget.skeleton";
import { ClaimWidgetSkeleton } from "./ClaimWidget";
import { ComparisonSkeleton } from "./Comparison.skeleton";
import { InlineTextInputSkeleton } from "./InlineTextInput";
import { IntakeSummarySkeleton } from "./IntakeSummary.skeleton";
import { MeducationWidgetSkeleton } from "./MeducationWidget.skeleton";
import { PatientProfileSkeleton } from "./PatientProfile.skeleton";
import { PricingBreakdownSkeleton } from "./PricingBreakdown.skeleton";
import { ProductCardSkeleton } from "./ProductCard.skeleton";
import { ProgressBarSkeleton } from "./ProgressBar";
import { ReasoningTraceSkeleton } from "./ReasoningTrace.skeleton";
import { TelehealthConsentWidgetSkeleton } from "./TelehealthConsentWidget.skeleton";
import { TestimonialWidgetSkeleton } from "./TestimonialWidget.skeleton";
import { WeightInputWidgetSkeleton } from "./WeightInputWidget.skeleton";

/**
 * Tool name -> per-widget loading skeleton. Tools not in this map fall back
 * to {@link DefaultWidgetSkeleton} (a generic "preparing..." placeholder).
 *
 * Modal/sheet-rendered widgets (`show_form`, `show_checkout`) use the fallback
 * because there's no inline widget shape to mirror. `show_auth` has a
 * dedicated skeleton that matches {@link AuthInlineCard}.
 */
const SKELETON_REGISTRY: Record<string, React.ComponentType> = {
  show_auth: AuthWidgetSkeleton,
  show_chips: ChipsWidgetSkeleton,
  show_chip_group: ChipGroupWidgetSkeleton,
  show_bmi: BMIResultWidgetSkeleton,
  show_product: ProductCardSkeleton,
  show_pricing: PricingBreakdownSkeleton,
  show_claim: ClaimWidgetSkeleton,
  show_testimonial: TestimonialWidgetSkeleton,
  show_comparison: ComparisonSkeleton,
  show_meducation: MeducationWidgetSkeleton,
  show_intake_summary: IntakeSummarySkeleton,
  show_patient_profile: PatientProfileSkeleton,
  show_text_input: InlineTextInputSkeleton,
  show_weight_input: WeightInputWidgetSkeleton,
  show_consent: TelehealthConsentWidgetSkeleton,
  show_progress: ProgressBarSkeleton,
  show_reasoning_trace: ReasoningTraceSkeleton,
};

const FALLBACK_LABELS: Record<string, string> = {
  show_form: "form",
  show_checkout: "checkout",
};

/**
 * Generic placeholder used when no per-widget skeleton is registered.
 * Keeps the existing "Preparing X..." affordance for modal/sheet widgets.
 */
function DefaultWidgetSkeleton({ toolName }: { toolName: string }) {
  const label = FALLBACK_LABELS[toolName] ?? "your next step";
  return (
    <div className="rounded-3xl bg-widget-bg/40 p-4 shadow-sm w-full">
      <div className="mb-3 flex items-center gap-2 text-sm text-text-secondary">
        <div className="h-2 w-2 rounded-full bg-accent animate-pulse" />
        <span>Preparing {label}...</span>
      </div>
      <SkeletonStack>
        <SkeletonLine $size="text" $width="md" />
        <SkeletonBlock $h="sm" $radius="lg" />
        <SkeletonBlock $h="sm" $radius="lg" className="w-4/5" />
      </SkeletonStack>
    </div>
  );
}

/**
 * Renders the appropriate loading skeleton for a widget tool. Designed to
 * be passed as `renderLoading` to {@link ChatFeed}.
 */
export function WidgetLoadingRegistry({ item }: { item: ChatLoadingItem }) {
  const canonical = String(resolveToolName(item.toolName));
  const Skeleton = SKELETON_REGISTRY[canonical];
  return (
    <div className="w-full animate-fade-in" aria-busy="true">
      {Skeleton ? <Skeleton /> : <DefaultWidgetSkeleton toolName={canonical} />}
      <span className="sr-only">Loading</span>
    </div>
  );
}
